import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { FormProvider, useForm } from "react-hook-form";

import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper, { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { format, setMinutes } from "date-fns";
import { merge } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { getDefaultSubject } from "components/email-form/email.utils";
import ErrorSnackbarContent from "components/error-snackbar-content";
import SubmitButton from "components/modal-form/submit-button.component";
import {
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_TEXT_SMS
} from "constants/patients";
import {
  TOUCHPOINT_OUTCOME_UNKNOWN,
  TOUCHPOINT_STATUS_UPCOMING,
  TOUCHPOINT_TYPE_MANUAL
} from "constants/touchpoints";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { useCreateTouchpointMutation } from "services/touchpoints";
import type { Patient } from "services/types/patients.type";
import { normalizeTemplateValue } from "utils/message-templates.util";

import {
  setCustomMessage,
  setOpen
} from "../action-snackbar/action-snackbar-slice";
import TouchpointFormContent, {
  TouchpointMessageFieldRef
} from "./touchpoint-form-content";

export type NewTouchpointFormValues = {
  patient: any; // Typescript out of memory issue with type Patient
  communication_method: string | number | null;
  assigned_to: { label: string; value: string } | null;
  execution_date: string;
  execution_time: any;
  message: string;
  notes: string;
  expected_revenue: string;
  email_subject: string;
};

type EmailFormProps = {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
  selectedFiles?: any[];
  initialDocuments?: File[];
};

const DEFAULT_VALUES: NewTouchpointFormValues = {
  patient: null,
  email_subject: "",
  communication_method: null,
  assigned_to: null,
  execution_date: "",
  execution_time: new Date(setMinutes(new Date(), 0)),
  message: "",
  notes: "",
  expected_revenue: ""
};

function PaperComponent(props: PaperProps) {
  const nodeRef = useRef(null);
  return (
    <Draggable
      handle="#draggable-dialog-new-touchpoint"
      cancel={'[class*="MuiDialogContent-root"]'}
      nodeRef={nodeRef}
    >
      <Paper {...props} elevation={1} ref={nodeRef} />
    </Draggable>
  );
}

export default function NewTouchpointForm({
  patient,
  isOpen,
  onClose
}: EmailFormProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);

  const [createTouchpoint, { isLoading }] = useCreateTouchpointMutation();

  // Ref to access email builder export function
  const touchpointMessageFieldRef = useRef<TouchpointMessageFieldRef>(null);

  const defaultValues: NewTouchpointFormValues = useMemo(() => {
    return merge(DEFAULT_VALUES, { patient });
  }, [patient]);

  const formMethods = useForm<NewTouchpointFormValues>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES
  });

  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { isValid }
  } = formMethods;

  useEffect(() => {
    setValue("patient", patient);
  }, [setValue, patient]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [communication_method] = watch(["communication_method"]);

  const isEmail = Number(communication_method) === COMMUNICATION_METHOD_EMAIL;
  const isSms = Number(communication_method) === COMMUNICATION_METHOD_TEXT_SMS;
  const showMessageField = isSms || isEmail;

  const [subjectChanged, setSubjectChanged] = useState(false);

  const clinicName =
    currentClinic?.display_name ??
    patient?.preferred_clinic?.display_name ??
    currentClinic?.practice?.name;

  const handlePatientSelected = useCallback(() => {
    if (subjectChanged) return;

    const subject = getDefaultSubject(clinicName);
    setValue("email_subject", subject);
    setSubjectChanged(true);
  }, [clinicName, setValue, subjectChanged]);

  const handleEmailSubjectChanged = useCallback(() => {
    setSubjectChanged(true);
  }, []);

  useEffect(() => {
    if (!isEmail) {
      setValue("email_subject", "");
      setSubjectChanged(false);
    } else {
      handlePatientSelected();
    }
  }, [isEmail, setValue, handlePatientSelected]);

  useEffect(() => {
    trigger("message");
  }, [trigger, showMessageField]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
    setSubjectChanged(false);
  }, [onClose, reset]);

  const onSubmit = async (data: NewTouchpointFormValues) => {
    const time = format(new Date(data.execution_time), "hh:mm aaa");
    let message: string = "";
    let email_subject: string | undefined = undefined;
    let email_design_json: UnlayerDesign | undefined = undefined;

    if (COMMUNICATION_METHOD_TEXT_SMS === Number(data.communication_method)) {
      message = normalizeTemplateValue(data.message) ?? "";
    } else if (
      COMMUNICATION_METHOD_EMAIL === Number(data.communication_method)
    ) {
      email_subject = data.email_subject ? data.email_subject : undefined;

      // Export HTML from email builder if in builder mode
      const emailBuilderData =
        await touchpointMessageFieldRef.current?.exportHtml();
      if (emailBuilderData) {
        message = emailBuilderData.html;
        email_design_json = emailBuilderData.design;
      } else {
        message = normalizeTemplateValue(data.message, false) ?? "";
      }
    }

    createTouchpoint({
      patient: data.patient?.id ?? patient?.id,
      clinic: currentClinic.id,
      email_subject: email_subject,
      assigned_to: data?.assigned_to
        ? Number(data.assigned_to?.value)
        : undefined,
      communication_method: Number(data.communication_method),
      execution_time: data.execution_date
        ? new Date(`${data.execution_date} ${time || ""}`.trim()).toISOString()
        : null,
      outcome: TOUCHPOINT_OUTCOME_UNKNOWN,
      type: TOUCHPOINT_TYPE_MANUAL,
      status: TOUCHPOINT_STATUS_UPCOMING,
      message,
      notes: data.notes,
      expected_revenue: Number(data.expected_revenue || 0),
      email_design_json
    }).then((response) => {
      if ("error" in response) {
        dispatch(
          setCustomMessage(<ErrorSnackbarContent response={response} />)
        );
        dispatch(setOpen(true));
      } else {
        dispatch(setCustomMessage("Touchpoint created"));
        dispatch(setOpen(true));
        handleClose();
      }
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperComponent={PaperComponent}
      fullWidth
      maxWidth="xl"
      hideBackdrop={false}
      data-testid="draggable-dialog-new-touchpoint"
      aria-labelledby="draggable-dialog-new-touchpoint"
      PaperProps={{
        sx: {
          boxShadow: theme.shadows[3]
        }
      }}
      className="animate__animated animate__fadeIn animate__faster"
      disableEnforceFocus={false}
    >
      <form id="new-touchpoint">
        <DialogTitle
          style={{ cursor: "move", padding: 8 }}
          id="draggable-dialog-new-touchpoint"
          bgcolor={theme.palette.lightBackground}
        >
          <Stack
            flexDirection="row"
            alignContent="center"
            justifyContent="space-between"
            width="100%"
          >
            <Stack flexDirection="row" justifyContent="space-between">
              <IconButton>
                <DragIndicatorIcon />
              </IconButton>
              <Box display="flex" alignItems="center">
                <Typography variant="h5" component="div">
                  {`New Touchpoint${patient ? ` for ${patient.fullName}` : ""}`}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} data-testid="close-modal-button">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pb: 0, mb: -3, mt: -1 }}>
          <FormProvider {...formMethods}>
            <TouchpointFormContent
              ref={touchpointMessageFieldRef}
              showMessageField={showMessageField}
              defaultPatient={patient}
              onPatientSelected={handlePatientSelected}
              onEmailSubjectChanged={handleEmailSubjectChanged}
            />
          </FormProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 1, mt: 3 }}>
          <SubmitButton
            submitButtonLabel="Save"
            formName="new-touchpoint"
            submitIsDisabled={!isValid}
            isSubmitting={isLoading}
            onSubmit={handleSubmit(onSubmit)!}
            control={control}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
